import { supabase } from "@/integrations/supabase/client";

// client_errors isn't in the generated Supabase types yet — same situation as
// community_self_profiles (see src/hooks/use-my-community.ts) until types are
// regenerated against the migration.

type ErrorSource = "react-error-boundary" | "window-error" | "unhandled-rejection";

// Errors thrown by scripts the browser injects into the page, not by our code.
// The big one is Meta's in-app browser (Instagram/Facebook on Android), whose
// `navigation_performance_logger_android` bundle calls a native postMessage
// bridge that throws whenever the WebView is being torn down — on every soft
// navigation and on every page close. It fires from `iabjs://`, a scheme that
// isn't ours, we can't patch it, and it has no effect on the visitor. Left
// unfiltered it's the single loudest entry in the Bugs tab.
const THIRD_PARTY_NOISE = [
  // Meta in-app browser bridge (iabjs:// / navigation_performance_logger_android).
  /Error invoking postMessage/i,
  /iabjs:\/\//i,
  /navigation_performance_logger/i,
  // Benign layout-loop warning Chrome reports as an uncaught error.
  /ResizeObserver loop/i,
];

/**
 * True when the error comes from an injected third-party script rather than
 * from Ernest, so it should never reach the Bugs tab.
 */
export const isThirdPartyNoise = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  const stack = error instanceof Error ? (error.stack ?? "") : "";
  const haystack = `${message}\n${stack}`;
  return THIRD_PARTY_NOISE.some((pattern) => pattern.test(haystack));
};

/** Fire-and-forget: logging a bug must never itself throw or block the UI. */
export const logClientError = (
  source: ErrorSource,
  error: unknown,
  extra?: { componentStack?: string },
) => {
  // The dev server points at the same Supabase project as production —
  // there's no separate staging DB. Without this guard, every local `npm
  // run dev` session (including automated browser testing) writes into the
  // same client_errors table the admin Bugs tab reads, indistinguishable
  // from a real visitor's crash.
  if (import.meta.env.DEV) {
    console.warn(`[logClientError:${source}] (not sent — dev mode)`, error);
    return;
  }
  if (isThirdPartyNoise(error)) return;
  void (async () => {
    try {
      const message = error instanceof Error ? error.message : String(error);
      const stack = error instanceof Error ? (error.stack ?? null) : null;
      const { data } = await supabase.auth.getSession();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any).from("client_errors").insert({
        source,
        message,
        stack,
        component_stack: extra?.componentStack ?? null,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        user_id: data.session?.user.id ?? null,
      });
    } catch {
      // Nothing to fall back to — swallow so a logging failure can't cascade.
    }
  })();
};
